
import { Component, Vue } from "vue-property-decorator";
import Sidebar from "@/components/navigations/Sidebar.vue";
import Alert from "@/components/alert/Alert.vue";
import Cta from "@/components/buttons/Button.vue";
import { login } from "@/store";
import { User } from "@/models";

interface Service {
  title: string;
  description: string;
  icon: string;
  /** Clé de couleur de la tuile d'icône (voir `.service__icon--*`). */
  color: string;
  /** Route interne... */
  to?: string;
  /** ...ou lien externe (ouvert dans un nouvel onglet). */
  href?: string;
  badge?: string;
  /** Rôles/habilitations requis (au moins un) ; visible par tous si absent. */
  access?: string[];
}

interface Section {
  title: string;
  services: Service[];
}

const SECTIONS: Section[] = [
  {
    title: "Recensement",
    services: [
      {
        title: "eOPE",
        description:
          "L'outil de la DO : ton recensement et tes disponibilités sont désormais ici.",
        icon: "far fa-clipboard-list",
        color: "garde",
        href: "https://eope.protectioncivile.org",
      },
      {
        title: "Timeline v2",
        description: "Le recensement d'antenne, avant le relais d'eOPE.",
        icon: "far fa-check-circle",
        color: "primary",
        to: "/timeline",
      },
    ],
  },
  {
    title: "Outils d'antenne",
    services: [
      {
        title: "Vérifications",
        description: "Vérification assistée des lots de matériel.",
        icon: "far fa-backpack",
        color: "dps",
        to: "/verification-assistee",
      },
      {
        title: "Photothèque",
        description: "Les albums photos de l'antenne.",
        icon: "far fa-images",
        color: "exercice",
        to: "/go-photos",
      },
      {
        title: "Wiki",
        description: "Procédures, fiches et documentation.",
        icon: "far fa-books",
        color: "for",
        to: "/go-wiki",
      },
      {
        title: "Suivi des bénévoles",
        description: "Fiches, compétences et habilitations.",
        icon: "far fa-users",
        color: "maraude",
        to: "/administration/benevoles",
        access: ["ADMIN", "USR:VIEW", "USR:EDIT", "USR:COMP", "USR:HAB"],
      },
    ],
  },
];

@Component({
  components: { Sidebar, Alert, Cta },
})
/** Page d'accueil — portail de cartes vers les services de l'antenne (recensement, outils), filtrées selon les droits. */
export default class Home extends Vue {
  private openEope(): void {
    window.open(
      "https://eope.protectioncivile.org",
      "_blank",
      "noopener,noreferrer"
    );
  }

  get context(): User | null {
    return login.getContext;
  }

  /** Sections avec uniquement les services accessibles à l'utilisateur. */
  get visibleSections(): Section[] {
    return SECTIONS.map((section) => ({
      ...section,
      services: section.services.filter(
        (s) => !s.access || this.hasAccess(s.access)
      ),
    }));
  }

  /** Whether the current user has any of the given roles or habilitations. */
  private hasAccess(allowed: string[]): boolean {
    const ctx = this.context;
    if (!ctx) return false;
    return allowed.some(
      (r) => ctx.roles.includes(r) || ctx.habilitations.includes(r)
    );
  }

  private linkProps(service: Service): Record<string, string> {
    return service.href
      ? { href: service.href, target: "_blank", rel: "noopener noreferrer" }
      : { to: service.to ?? "/" };
  }
}
